import axios from "axios";
import { getAuthToken } from "./authService";
import { getEnv } from "../env.js";

const BACKEND = getEnv("VITE_BACKEND") + "/api/";

/**
 * List of endpoints that don't require authentication
 */
const unauthenticated_endpoints = [
  "auth/login",
  "auth/reset",
  "auth/resetrequest",
  "auth/refresh-token",
  "version",
];

/**
 * List of 2FA challenge endpoints that use challenge tokens instead of access tokens
 * These endpoints should not have their Authorization header overridden by the interceptor
 */
const two_factor_challenge_endpoints = [
  "auth/2fa/request-challenge",
  "auth/2fa/verify",
];

/**
 * List of endpoints that require credentials, i.e. cookies
 * to be included in the request because they need to handle
 * the refresh token cookie
 */
const credentialed_endpoints = [
  "auth/logout",
  "auth/refresh-token",
  "auth/login",
  "auth/reset",
  "auth/2fa/verify",
];

// Create an axios instance with interceptors for token handling
export const api = axios.create({
  baseURL: BACKEND,
});

// Request interceptor to add the current access token to every request
api.interceptors.request.use(
  async (config) => {
    // Skip adding token for auth endpoints that don't need it
    const is_auth_endpoint = unauthenticated_endpoints.includes(config.url);
    const is_2fa_challenge_endpoint = two_factor_challenge_endpoints.includes(
      config.url
    );
    const is_credentialed_endpoint = credentialed_endpoints.includes(
      config.url
    );

    // Don't override Authorization header for 2FA challenge endpoints
    // They use challenge tokens set by twoFactorService.js
    if (!is_auth_endpoint && !is_2fa_challenge_endpoint && !config.skipAuth) {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    if (is_credentialed_endpoint) {
      config.withCredentials = true;
    }

    // Dev bypass for the Apache maintenance gate (ops/deploy/hosted/maintenance/):
    // most requests don't send cookies, so forward the bypass cookie as a header.
    const maintenance_bypass = document.cookie.match(/(?:^|;\s*)maint_bypass=([^;]+)/);
    if (maintenance_bypass) {
      config.headers["X-Maintenance-Bypass"] = maintenance_bypass[1];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // console.log("response interceptor error", error.config.url, error.message);
    const original_request = error.config;

    // A maintenance window started while this tab was open. The frontend host is
    // gated by the same flag, so reloading lands on the maintenance page.
    if (error.response?.status === 503 && error.response.headers["x-maintenance"]) {
      window.location.reload();
      return Promise.reject(error);
    }

    // Skip auth handling for auth endpoints, 2FA challenge endpoints,
    // explicitly skipped requests, or already retried requests
    const is_auth_endpoint = unauthenticated_endpoints.includes(
      original_request.url
    );
    const is_2fa_challenge_endpoint = two_factor_challenge_endpoints.includes(
      original_request.url
    );
    if (
      is_auth_endpoint ||
      is_2fa_challenge_endpoint ||
      original_request.skipAuth ||
      original_request._retry
    ) {
      return Promise.reject(error);
    }

    // Handle token authentication expiration errors (401/403)
    if (
      error.response &&
      (error.response.status === 401 || error.response.status === 403)
    ) {
      original_request._retry = true; // Mark to prevent infinite loops

      try {
        // getAuthToken from authService, will use refreshTokenPromise
        // to handle concurrent refreshes. Force refresh on 401/403.
        const new_token = await getAuthToken(true);

        if (new_token) {
          // Retry the original request with the new token
          original_request.headers.Authorization = `Bearer ${new_token}`;
          return axios(original_request); // Retry with basic axios
        } else {
          // refresh failed. authService.refreshToken should have called redirectToLogin.
          // Propagate the original error; user will be redirected.
          return Promise.reject(error);
        }
      } catch (refresh_related_error) {
        // this catch is for unexpected errors from getAuthToken itself. although
        // authService.refreshToken aims to handle its errors and call redirectToLoginm
        // we could optionally redirect to loigin here, if something unexpected happened:
        // redirectToLogin();
        return Promise.reject(refresh_related_error); // Or propagate the original error
      }
    }

    // for all other errors, just reject the promise
    return Promise.reject(error);
  }
);

/**
 * Logs in with the given email and password.
 * @param {string} email the email
 * @param {string} password the password
 * @param {Object} [callbacks]
 * @param {Function} [callbacks.setIsLoading] whether to set loading state
 * @param {Function} [callbacks.onSuccess] callback with the response data when successful
 * @param {Function} [callbacks.onError] callback with the error when failing
 * @returns {Promise<Object | null>} the user data on success, null on failure
 */
export const requestLogin = async (email, password, callbacks = {}) => {
  const { setIsLoading, onSuccess, onError, onTwoFactorRequired } = callbacks;

  if (setIsLoading) setIsLoading(true);

  try {
    const response = await api.post(
      `auth/login`,
      { email, password },
      { withCredentials: true }
    );

    if (response.status === 200 && onSuccess) {
      onSuccess(response.data.data);
    } else if (response.status === 202 && response.data.requires_2fa) {
      // 2FA is required
      if (onTwoFactorRequired) {
        onTwoFactorRequired(response.data);
      }
    }

    return response.data;
  } catch (error) {
    if (onError) onError(error);
    return null;
  } finally {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }
};

export const requestLogout = async () => {
  return await api.post("auth/logout");
};

export const getAppVersion = async () => {
  try {
    const response = await api.get("version");
    return response.data;
  } catch (error) {
    console.error("Error fetching app version:", error);
    return null;
  }
};

export const requestTokenRefresh = async () => {
  return await api.post("auth/refresh-token");
};

export const requestPasswordReset = async (email, callbacks = {}) => {
  const { setIsLoading, onSuccess, onError } = callbacks;

  if (setIsLoading) setIsLoading(true);

  try {
    const response = await api.post(`auth/resetrequest`, { email });

    if (response.status === 200 && onSuccess) {
      onSuccess(response);
    }

    return response;
  } catch (error) {
    if (onError) onError(error);
    return null;
  } finally {
    if (setIsLoading) setIsLoading(false);
  }
};

export const getResetTokenEmail = async (token, callbacks = {}) => {
  const { onSuccess, onError } = callbacks;

  try {
    const response = await api.get(`auth/reset/${token}`, { skipAuth: true });

    if (response.status === 200 && onSuccess) {
      onSuccess(response);
    }

    return response;
  } catch (error) {
    if (onError) onError(error);
    return null;
  }
};

export const resetPassword = async (token, password, callbacks = {}) => {
  const { setIsLoading, onSuccess, onError } = callbacks;

  if (setIsLoading) {
    setIsLoading(true);
  }

  try {
    const response = await api.post(`auth/reset`, {
      token,
      password,
    });

    const { status, data } = response;

    if (status === 200) {
      if (onSuccess) {
        await onSuccess(response);
      }
    }

    return data;
  } catch (error) {
    handleApiError(error, callbacks);
    if (onError) {
      onError(error);
    }
    return null;
  } finally {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }
};

/**
 * Makes an authenticated GET request to the backend API
 *
 * @param {string} route  - API endpoint route to call
 * @param {Object} props - The properties for the GET request
 * @param {string} props.label - Descriptive label for the request, used in error messages
 * @param {Function} [props.setIsLoading] - State setter function to track loading status
 * @param {Function} [props.setResult] - State setter function to store the response data
 * @param {Function} [props.enqueueSnackbar] - Function to display notifications (from notistack)
 * @param {Function} [props.onError] - Called with the raw axios error when the request fails (not on cancellation)
 * @param {Object} [props.options=null] - Additional axios request options (params, headers, etc.)
 * @param {AbortSignal} [props.signal] - Optional AbortSignal to cancel the request
 * @returns {Promise<any|null>} - Returns response data if successful, null otherwise
 */
export const backendGet = async (route, props = {}) => {
  const {
    label,
    setIsLoading,
    setResult,
    enqueueSnackbar,
    onError,
    options = null,
    signal,
  } = props;

  if (setIsLoading) {
    setIsLoading(true);
  }

  try {
    const response = await api.get(route, { ...options, signal });
    if (response.status === 200) {
      if (setResult) {
        setResult(response.data);
      }
      return response.data;
    } else {
      if (enqueueSnackbar) {
        enqueueSnackbar(
          "Daten (" + label + ") konnten nicht abgerufen werden.",
          { variant: "error" }
        );
      }
      return null;
    }
  } catch (error) {
    const isCanceled =
      error?.name === "AbortError" ||
      error?.name === "CanceledError" ||
      error?.code === "ERR_CANCELED";

    if (isCanceled) {
      console.warn(`Request for ${label} was canceled.`);
    } else if (onError) {
      onError(error);
    } else if (enqueueSnackbar) {
      enqueueSnackbar("Daten (" + label + ") konnten nicht abgerufen werden.", {
        variant: "error",
      });
    }
    console.error("Error in backendGet:", error);
    return null;
  } finally {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }
};

/**
 * Handles API errors, including special handling for 422 validation errors.
 * If a `setError` function (from react-hook-form) is provided in props, it will
 * automatically populate form fields with validation messages.
 *
 * @param {Error} error - The error object from the catch block.
 * @param {Object} props - The props object passed to the original backend function.
 * @param {string} props.label - A label for the action, used in generic error messages.
 * @param {Function} [props.setError] - The `setError` function from `react-hook-form`.
 * @param {Function} [props.enqueueSnackbar] - The `enqueueSnackbar` function from `notistack`.
 * @throws {Error} - Re-throws the error if it's a 422 error and no `setError` handler is provided.
 */
const handleApiError = (error, props) => {
  const { label, setError, enqueueSnackbar, onError } = props;
  const action_label = `Aktion (${label})` || "Aktion";

  const extractErrorMessage = (error_to_parse) => {
    const response_data = error_to_parse?.response?.data;

    if (typeof response_data === "string") {
      return response_data;
    }

    if (response_data?.message) {
      return response_data.message;
    }

    if (response_data?.error) {
      return response_data.error;
    }

    const validation_errors = response_data?.errors;
    if (validation_errors && typeof validation_errors === "object") {
      const first_key = Object.keys(validation_errors)[0];
      const first_value = validation_errors[first_key];

      if (Array.isArray(first_value) && first_value[0]) {
        return first_value[0];
      }

      if (typeof first_value === "string") {
        return first_value;
      }
    }

    return null;
  };

  const isCanceled =
    error?.name === "AbortError" ||
    error?.name === "CanceledError" ||
    error?.code === "ERR_CANCELED";

  if (isCanceled) {
    console.warn(`Request for ${action_label} was canceled.`);
  }

  if (onError) {
    return onError(error);
  }

  // Handle 422 Unprocessable Entity (Validation Errors)
  if (error.response && error.response.status === 422) {
    const validationErrors = error.response.data?.errors;

    // If a form's setError handler is provided, use it automatically
    if (setError && validationErrors && typeof validationErrors === "object") {
      for (const field in validationErrors) {
        setError(field, {
          type: "manual",
          message: validationErrors[field][0],
        });
      }
      // The error is handled, no need to show a generic snackbar or re-throw
      return;
    }

    const backend_message = extractErrorMessage(error);
    if (backend_message && enqueueSnackbar) {
      enqueueSnackbar(backend_message, { variant: "error" });
      return;
    }

    // For backward compatibility, re-throw if no setError handler is present
    throw error;
  }

  if (error.response && error.response.status === 403) {
    const backend_message = extractErrorMessage(error);
    if (backend_message && enqueueSnackbar) {
      enqueueSnackbar(backend_message, { variant: "error" });
      return;
    }
  }

  // Generic error handling for all other cases
  if (enqueueSnackbar) {
    enqueueSnackbar(`${action_label} konnte nicht ausgeführt werden.`, {
      variant: "error",
    });
  }
  console.error(`Error in API call (${action_label}):`, error);
};

/**
 * A generic handler for successful API responses. It optionally shows a success
 * notification and calls a success callback.
 *
 * @param {Object} response - The successful API response.
 * @param {Object} props - The props from the original API call.
 * @param {string} [props.successMessage] - Message to show in a success snackbar.
 * @param {Function} [props.onSuccess] - Callback to execute on success.
 * @param {Function} [props.enqueueSnackbar] - The notistack enqueueSnackbar function.
 * @param {Function} [props.setResult] - State setter to store the response data.
 */
const handleApiSuccess = (response, props) => {
  const { successMessage, onSuccess, enqueueSnackbar, setResult } = props;

  if (setResult) {
    setResult(response.data);
  }

  if (successMessage && enqueueSnackbar) {
    enqueueSnackbar(successMessage, { variant: "success" });
  }

  if (onSuccess) {
    onSuccess(response.data);
  }
};

/**
 * Performs a POST request to the backend API.
 *
 * @param {String} route API endpoint to which to send the request.
 * @param {Object} props Properties for the POST request.
 * @param {String} props.label A label for the request, used for error messages.
 * @param {Function} [props.setIsLoading] Function to set a loading state.
 * @param {Object} props.toSend Data to send in the request body.
 * @param {Function} [props.setResult] Function to set the result of the request.
 * @param {Function} [props.enqueueSnackbar] Function to enqueue a snackbar.
 * @param {Function} [props.setError] Optional `setError` function from `react-hook-form` for automatic validation error handling.
 * @param {string} [props.successMessage] Optional success message to display in a snackbar.
 * @param {Function} [props.onSuccess] Optional callback to run on successful request.
 * @param {AbortSignal} [props.signal] Optional AbortSignal to cancel the request.
 * @param {Object} [props.options={}] Additional axios request options (headers, params, responseType, etc.).
 *
 * @returns {Promise<Object | null>} The response data from the API, or null on failure.
 */
export const backendPost = async (route, props) => {
  const {
    setIsLoading,
    toSend,
    signal,
    options = {},
    onSuccess,
    throwOnFailure = false,
  } = props;

  if (setIsLoading) {
    setIsLoading(true);
  }

  try {
    const response = await api.post(route, toSend, { signal, ...options });
    if (response.status === 200 || response.status === 201) {
      handleApiSuccess(response, props);
      return response.data;
    }
    return null;
  } catch (error) {
    handleApiError(error, props);
    if (throwOnFailure) {
      throw error;
    } else {
      return null;
    }
  } finally {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }
};

export const backendPut = async (route, props) => {
  const { setIsLoading, toSend } = props;

  if (setIsLoading) {
    setIsLoading(true);
  }

  try {
    const response = await api.put(route, toSend);
    if (response.status === 200 || response.status === 201) {
      handleApiSuccess(response, props);
      return response.data;
    }
    return null;
  } catch (error) {
    handleApiError(error, props);
    throw error;
  } finally {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }
};

export const backendPatch = async (route, props) => {
  const { setIsLoading, toSend } = props;

  if (setIsLoading) {
    setIsLoading(true);
  }

  try {
    const response = await api.patch(route, toSend);
    if (response.status === 200 || response.status === 201) {
      handleApiSuccess(response, props);
      return response.data;
    }
    return null;
  } catch (error) {
    handleApiError(error, props);
    throw error;
  } finally {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }
};

export const backendDelete = async (route, props) => {
  const {
    label,
    setIsLoading,
    setResult,
    enqueueSnackbar,
    options = null,
  } = props;

  if (setIsLoading) {
    setIsLoading(true);
  }

  try {
    const response = await api.delete(route, options);
    if (response.status === 200) {
      if (setResult) setResult(response.data);
      return response.data;
    } else {
      if (enqueueSnackbar) {
        enqueueSnackbar(
          "Daten (" + label + ") konnten nicht gelöscht werden.",
          { variant: "error" }
        );
      }
      return null;
    }
  } catch (error) {
    if (enqueueSnackbar) {
      enqueueSnackbar("Daten (" + label + ") konnten nicht gelöscht werden.", {
        variant: "error",
      });
    }
    console.error("Error in backendDelete:", error);
    return null;
  } finally {
    if (setIsLoading) {
      setIsLoading(false);
    }
  }
};
